import React from 'react'
import type { ChartingRouteTab } from './chartingNoteOverlay.util'
import './ChartingSkeleton.css'

const TEETH_PER_ARCH = 16
const MIDLINE_AFTER = 8
const PERIO_TEETH = 16
const PERIO_METRIC_ROWS: {
  key: string
  kind: 'triple' | 'dots' | 'single'
}[] = [
  { key: 'PD', kind: 'triple' },
  { key: 'GM', kind: 'triple' },
  { key: 'CAL', kind: 'triple' },
  { key: 'Bleeding', kind: 'dots' },
  { key: 'Calculus', kind: 'dots' },
  { key: 'Supp', kind: 'dots' },
  { key: 'Furcation', kind: 'dots' },
  { key: 'Mobility', kind: 'single' },
]

const DENTAL_HISTORY_COLUMNS = ['12%', '22%', '18%', '18%', '10%', '12%']
const PERIO_HISTORY_COLUMNS = ['16%', '24%', '22%', '12%', '14%']
const PLAN_PROCEDURE_COLUMNS = ['10%', '26%', '14%', '10%', '12%', '12%', '16%']
const SIDE_PANEL_SECTIONS = 5
const PLAN_PHASES = 2
const PLAN_TABS = 2

type ChartingSkeletonProps = {
  variant?: ChartingRouteTab
}

const renderArch = (arch: 'upper' | 'lower'): React.ReactElement => (
  <div className={`charting-skeleton-arch charting-skeleton-arch--${arch}`}>
    {Array.from({ length: TEETH_PER_ARCH }).map((_, index) => {
      const isMolar = index < 3 || index > 12
      return (
        <React.Fragment key={`${arch}-${index}`}>
          {index === MIDLINE_AFTER ? (
            <span className="charting-skeleton-midline" />
          ) : null}
          <div
            className={`charting-skeleton-tooth-slot${
              isMolar ? ' charting-skeleton-tooth-slot--molar' : ''
            }`}
          >
            <div
              className={`charting-skeleton-tooth charting-skeleton-tooth--${arch}`}
            >
              <span className="charting-skeleton-tooth-num" />
              <span className="charting-skeleton-tooth-crown" />
              <span className="charting-skeleton-tooth-occlusal" />
            </div>
          </div>
        </React.Fragment>
      )
    })}
  </div>
)

const HistoryTableSkeleton: React.FC<{
  columns: string[]
  rows?: number
}> = ({ columns, rows = 5 }) => (
  <div className="charting-skeleton-table">
    <div className="charting-skeleton-header">
      {columns.map((width, colIndex) => (
        <div key={`header-${colIndex}`} className="charting-skeleton-cell">
          <span style={{ width }} />
        </div>
      ))}
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={`row-${rowIndex}`} className="charting-skeleton-row">
        {columns.map((width, colIndex) => (
          <div
            key={`row-${rowIndex}-col-${colIndex}`}
            className="charting-skeleton-cell"
          >
            <span style={{ width }} />
          </div>
        ))}
      </div>
    ))}
  </div>
)

const PerioCell: React.FC<{ kind: 'triple' | 'dots' | 'single' }> = ({
  kind,
}) => (
  <div
    className={`charting-skeleton-perio-cell charting-skeleton-perio-cell--${kind}`}
  >
    {kind === 'triple' ? (
      <>
        <span />
        <span />
        <span />
      </>
    ) : kind === 'dots' ? (
      <>
        <i />
        <i />
        <i />
      </>
    ) : (
      <span />
    )}
  </div>
)

const PerioSectionSkeleton: React.FC<{
  showToothNumbers?: boolean
  showDbm?: boolean
}> = ({ showToothNumbers = false, showDbm = false }) => (
  <div className="charting-skeleton-perio-section">
    {showToothNumbers ? (
      <div className="charting-skeleton-perio-row charting-skeleton-perio-row--teeth">
        <div className="charting-skeleton-perio-label" />
        {Array.from({ length: PERIO_TEETH }).map((_, index) => (
          <div key={`tooth-${index}`} className="charting-skeleton-perio-tooth">
            <span />
          </div>
        ))}
      </div>
    ) : null}
    <div className="charting-skeleton-perio-row charting-skeleton-perio-row--section">
      <span />
    </div>
    {showDbm ? (
      <div className="charting-skeleton-perio-row charting-skeleton-perio-row--dbm">
        <div className="charting-skeleton-perio-label" />
        {Array.from({ length: PERIO_TEETH }).map((_, index) => (
          <div key={`dbm-${index}`} className="charting-skeleton-perio-dbm">
            <span />
            <span />
            <span />
          </div>
        ))}
      </div>
    ) : null}
    {PERIO_METRIC_ROWS.map((row) => (
      <div key={row.key} className="charting-skeleton-perio-row">
        <div className="charting-skeleton-perio-label">
          <span />
        </div>
        {Array.from({ length: PERIO_TEETH }).map((_, index) => (
          <PerioCell key={`${row.key}-${index}`} kind={row.kind} />
        ))}
      </div>
    ))}
  </div>
)

const PerioChartSkeleton: React.FC = () => (
  <div className="charting-skeleton-chart charting-skeleton-chart--perio">
    <div className="charting-skeleton-perio">
      <PerioSectionSkeleton showToothNumbers showDbm />
      <PerioSectionSkeleton />
    </div>
  </div>
)

const OdontogramSkeleton: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => (
  <div
    className={`charting-skeleton-odontogram${
      compact ? ' charting-skeleton-odontogram--compact' : ''
    }`}
  >
    {renderArch('upper')}
    {renderArch('lower')}
  </div>
)

const DentalChartSkeleton: React.FC = () => (
  <div className="charting-skeleton-chart">
    <OdontogramSkeleton />
    <div className="charting-skeleton-toggle-rail">
      {[0, 1, 2].map((index) => (
        <span key={`toggle-${index}`} className="charting-skeleton-toggle" />
      ))}
    </div>
  </div>
)

const SidePanelSkeleton: React.FC = () => (
  <div className="charting-skeleton-panel">
    {Array.from({ length: SIDE_PANEL_SECTIONS }).map((_, index) => (
      <div
        key={`panel-${index}`}
        className={`charting-skeleton-panel-section${
          index === 0 ? ' charting-skeleton-panel-section--open' : ''
        }`}
      >
        <span className="charting-skeleton-panel-header" />
        {index === 0 ? (
          <div className="charting-skeleton-panel-body">
            <span />
            <span />
            <span />
          </div>
        ) : null}
      </div>
    ))}
  </div>
)

const PhaseBlockSkeleton: React.FC = () => (
  <div className="charting-skeleton-phase">
    <div className="charting-skeleton-phase-header">
      <span />
      <span />
      <span />
    </div>
    <HistoryTableSkeleton columns={PLAN_PROCEDURE_COLUMNS} rows={3} />
  </div>
)

const TreatmentPlanSkeleton: React.FC<{ showAddTab?: boolean }> = ({
  showAddTab = false,
}) => (
  <>
    <div className="charting-skeleton-plan-chart">
      <div className="charting-skeleton-plan-header">
        <span />
        <span />
      </div>
      <OdontogramSkeleton compact />
    </div>
    <div className="charting-skeleton-plan-handle" />
    <div className="charting-skeleton-plan-body">
      <div className="charting-skeleton-plan-tabs">
        {Array.from({ length: PLAN_TABS }).map((_, index) => (
          <span
            key={`tab-${index}`}
            className={`charting-skeleton-plan-tab${
              index === 0 ? ' charting-skeleton-plan-tab--active' : ''
            }`}
          />
        ))}
        {showAddTab ? (
          <span className="charting-skeleton-plan-tab charting-skeleton-plan-tab--add" />
        ) : null}
        {showAddTab ? (
          <span className="charting-skeleton-plan-progress" />
        ) : null}
      </div>
      <div className="charting-skeleton-plan-phases">
        {Array.from({ length: PLAN_PHASES }).map((_, index) => (
          <PhaseBlockSkeleton key={`phase-${index}`} />
        ))}
        {showAddTab ? (
          <div className="charting-skeleton-plan-summary">
            <span className="charting-skeleton-plan-summary-title" />
            <span />
            <span />
            <span />
          </div>
        ) : null}
      </div>
    </div>
  </>
)

const ChartingSkeleton: React.FC<ChartingSkeletonProps> = ({
  variant = 'home',
}) => {
  if (variant === 'perio') {
    return (
      <div className="charting-skeleton" aria-hidden="true">
        <PerioChartSkeleton />
        <HistoryTableSkeleton columns={PERIO_HISTORY_COLUMNS} />
      </div>
    )
  }

  if (variant === 'treatment-plan') {
    return (
      <div
        className="charting-skeleton charting-skeleton--plan"
        aria-hidden="true"
      >
        <TreatmentPlanSkeleton showAddTab />
      </div>
    )
  }

  if (variant === 'history') {
    return (
      <div
        className="charting-skeleton charting-skeleton--history"
        aria-hidden="true"
      >
        <TreatmentPlanSkeleton />
      </div>
    )
  }

  return (
    <div
      className="charting-skeleton charting-skeleton--home"
      aria-hidden="true"
    >
      <div className="charting-skeleton-home-row">
        <DentalChartSkeleton />
        <SidePanelSkeleton />
      </div>
      <HistoryTableSkeleton columns={DENTAL_HISTORY_COLUMNS} />
    </div>
  )
}

export default ChartingSkeleton
